/**
 * Il picker della libreria riscaldamenti, accanto al campo warmup del giorno.
 *
 * **Applicare un template copia il testo**, non lo collega. Un riferimento
 * vivo sembrerebbe più pulito, ma farebbe riscrivere programmi già pubblicati
 * e già letti dagli atleti ogni volta che qualcuno corregge un template:
 * l'allenamento di ieri cambierebbe da solo. Copiando, il giorno resta quello
 * che era quando è stato scritto.
 *
 * Il campo `day.warmup` **non cambia forma**: si aggiunge un modo per
 * riempirlo. È l'unico pezzo del piano tiro che tocca anche il dominio
 * fisico, ed è voluto — un riscaldamento non ha disciplina.
 *
 * Spec: docs/shooting/13-piano-revisione-test-federico.md §Gruppo G
 */
import { useEffect, useState } from "react";
import { BookmarkPlus, Library, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { fetchAPI } from "../../lib/utils";

export function WarmupLibraryPicker({ value, onApply, t }) {
	const [templates, setTemplates] = useState([]);
	const [loaded, setLoaded] = useState(false);
	const [saving, setSaving] = useState(false);

	// Si carica una volta sola e solo dove il picker compare: la lista dei
	// riscaldamenti è piccola ma la giornata si rende molte volte.
	useEffect(() => {
		let cancelled = false;
		fetchAPI("/warmups")
			.catch(() => [])
			.then((list) => {
				if (cancelled) return;
				setTemplates(Array.isArray(list) ? list : []);
				setLoaded(true);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const apply = (warmupId) => {
		const template = templates.find((item) => item.warmup_id === warmupId);
		if (!template) return;
		onApply(template.content || "");
	};

	/** Salva il testo del giorno come template riutilizzabile. */
	const saveCurrent = async () => {
		const content = (value || "").trim();
		if (!content) return;
		const name = window.prompt(t("pbWarmupSavePrompt"), content.slice(0, 40));
		if (!name?.trim()) return;
		setSaving(true);
		try {
			const created = await fetchAPI("/warmups", {
				method: "POST",
				body: JSON.stringify({ name: name.trim(), content }),
			});
			setTemplates((prev) => [...prev, created]);
			toast.success(t("pbWarmupSaved"));
		} catch (error) {
			toast.error(`${t("error")}: ${error.message}`);
		} finally {
			setSaving(false);
		}
	};

	if (!loaded) return null;

	return (
		<div className="flex items-center gap-1">
			{templates.length > 0 && (
				<Select value="" onValueChange={apply}>
					<SelectTrigger className="h-6 rounded-sm border-transparent bg-secondary/60 px-1.5 text-[10px] w-auto gap-1">
						<Library className="w-3 h-3 shrink-0" />
						<SelectValue placeholder={t("pbWarmupFromLibrary")} />
					</SelectTrigger>
					<SelectContent className="bg-card border-border">
						{templates.map((template) => (
							<SelectItem key={template.warmup_id} value={template.warmup_id}>
								{template.name}
								{template.library_scope === "global" ? " · Standard" : ""}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			)}
			{(value || "").trim() && (
				<Button
					type="button"
					variant="ghost"
					size="icon"
					className="h-6 w-6 rounded-sm text-muted-foreground"
					onClick={saveCurrent}
					disabled={saving}
					title={t("pbWarmupSaveToLibrary")}
				>
					{saving ? (
						<Loader2 className="w-3 h-3 animate-spin" />
					) : (
						<BookmarkPlus className="w-3 h-3" />
					)}
				</Button>
			)}
		</div>
	);
}
